// frontend/src/pages/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import AdminUpload from '../components/AdminUpload';
import VideoProcessing from '../components/VideoProcessing';
import api from '../services/api';
import { jsPDF } from 'jspdf'; // Import jsPDF
import '../App.css';

function AdminDashboard() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moderatorAnnotations, setModeratorAnnotations] = useState(null);
  const POLLING_INTERVAL = 5000; // Poll every 5 seconds

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/videos');
      // Sort videos by upload date (newest first)
      const sortedVideos = response.data.sort((a, b) =>
        new Date(b.uploadDate) - new Date(a.uploadDate)
      );

      // Check if the latest video has been annotated
      if (sortedVideos.length > 0) {
        const latestVideo = sortedVideos[0];
        if (
          latestVideo.status === 'Annotated' &&
          latestVideo.annotations &&
          latestVideo.annotations.length > 0
        ) {
          setModeratorAnnotations({
            videoId: latestVideo._id,
            annotations: latestVideo.annotations
          });
        }
      }

      setVideos(sortedVideos);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
    const interval = setInterval(fetchVideos, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handleUploadSuccess = () => {
    fetchVideos();
  };

  const handleProcessingComplete = () => {
    fetchVideos();
    // Clear the moderator annotations after processing is complete
    setModeratorAnnotations(null);
  };

  // Function to get status class for styling
  const getStatusClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'status-pending';
      case 'Annotated':
        return 'status-annotated';
      case 'Processed':
        return 'status-processed';
      default:
        return '';
    }
  };

  // New function to fetch annotations and generate/download the PDF report
  const handleDownloadCensorshipReport = async (videoId) => {
    try {
      // Fetch the video details (including annotations) from your backend
      const response = await api.get(`/videos/${videoId}`);
      const { annotations } = response.data;

      // Create a new PDF document
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Censorship Report", 10, 20);
      doc.setFontSize(12);

      let yPos = 30;
      if (annotations && annotations.length > 0) {
        annotations.forEach((annotation, index) => {
          const reportText = `Annotation ${index + 1}: Time: ${annotation.startTime} - ${annotation.endTime} | Note: ${annotation.note}`;
          doc.text(reportText, 10, yPos);
          yPos += 10;
          // If nearing the bottom of the page, add a new page
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
        });
      } else {
        doc.text("No annotations found for this video.", 10, yPos);
      }

      // Trigger the PDF download
      doc.save("censorship_report.pdf");
    } catch (error) {
      console.error('Error generating censorship report:', error);
    }
  };

  // Get only the latest video
  const latestVideo = videos.length > 0 ? videos[0] : null;

  return (
    <div className="dashboard-container">
      <AdminUpload onUploadSuccess={handleUploadSuccess} />
      
      <div className="container">
        <h2 className="section-header">Content Library</h2>
        
        {loading && videos.length === 0 ? (
          <div className="loading-indicator">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading videos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-film"></i>
            <p>No videos uploaded yet. Upload your first video above.</p>
          </div>
        ) : (
          <div className="latest-video-container">
            <div className="video-card">
              <div className="video-info">
                <h3>{latestVideo.title}</h3>
                <p>Uploaded: {new Date(latestVideo.uploadDate).toLocaleDateString()}</p>
                <span className={`video-status ${getStatusClass(latestVideo.status)}`}>
                  {latestVideo.status}
                </span>
                
                {latestVideo.status === 'Processed' && latestVideo.processedFileUrl && (
                  <div className="video-actions">
                    <a
                      href={latestVideo.processedFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-primary"
                    >
                      <i className="fas fa-play-circle"></i> View Processed
                    </a>
                    <a
                      href={latestVideo.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-secondary"
                    >
                      <i className="fas fa-eye"></i> View Original
                    </a>
                    <a
                      href="http://127.0.0.1:5000"
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-secondary"
                    >
                      <i className="fas fa-volume-mute"></i> Censor the Audio
                    </a>
                    {/* New button for downloading the censorship report as a PDF */}
                   
                  </div>
                )}
              </div>
            </div>
            
            {latestVideo.status === 'Pending' && (
              <VideoProcessing 
                video={latestVideo} 
                onProcessingComplete={handleProcessingComplete}
                moderatorAnnotations={moderatorAnnotations}
              />
            )}
            
            {latestVideo.status === 'Annotated' && (
              <VideoProcessing 
                video={latestVideo} 
                onProcessingComplete={handleProcessingComplete}
                moderatorAnnotations={moderatorAnnotations}
                autoStart={true}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
